import { patchAutomation, patchAutomationFolder } from '@kizen/api/automation';
import { Global, css } from '@emotion/core';
import {
  type FC,
  memo,
  type PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  type AutomationOrFolder,
  editFolderSuccess,
  getAutomationsAndFolders,
  selectIsFetchingFolders,
  type Folder,
} from 'store/automationsPage/automations.redux';
import AxiosService, { getOriginalError } from 'services/AxiosService';
import { type FolderStruct } from '@kizen/kds/FoldersTree';
import { MAX_FOLDER_LEVEL } from '../AutomationFolders/constants';
import { checkNestedFoldersLevels, isFolder } from './helpers';
import { useTranslation } from 'react-i18next';
import { toastVariant, useToast } from '__components/ToastProvider';
import { getErrorMessage } from '__hooks/useErrors';
import { FoldersDragAndDropContext } from './context';

const GlobalDraggingStyles = memo(Global);

const styles = css`
  *,
  *::before,
  *::after {
    cursor: grabbing !important;
    user-select: none !important;
  }
`;

export const FoldersDragAndDropProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showToast] = useToast();

  const isLoadingFolders = useSelector(selectIsFetchingFolders);

  const [dragging, setDragging] = useState(false);
  const [isPatching, setIsPatching] = useState(false);
  const [dragItem, setDragItem] = useState<AutomationOrFolder | null>(null);
  const [dropFolder, setDropFolder] = useState<FolderStruct<Folder> | null>(
    null
  );

  const patching = isPatching || isLoadingFolders;
  const isLocked = patching || dragging;

  const notAllowedMessage = useMemo(() => {
    if (!dragItem || !dropFolder) {
      return null;
    }

    if (!isFolder(dragItem!)) {
      return dragItem.folder?.id !== dropFolder.id
        ? null
        : t('This item is already here. Choose another location.');
    }

    const folderToDrop = dropFolder.folderById[dragItem.id];

    if (!folderToDrop) {
      return t('Nothing was dropped. Please try again.');
    }

    if (folderToDrop.id === dropFolder.id) {
      return t('You can’t move a folder into itself. Choose another location.');
    }
    if (folderToDrop.parentFolder?.id === dropFolder.id) {
      return t('This item is already here. Choose another location.');
    }
    if (folderToDrop.descendantIds.has(dropFolder.id)) {
      return t(
        'You can’t move a folder into one of its subfolders. Choose another location.'
      );
    }

    const allowedLevel = MAX_FOLDER_LEVEL - dropFolder.level;
    if (allowedLevel <= 0) {
      return t(
        'Folders can only be nested two levels deep. Choose another location.'
      );
    }
    if (!checkNestedFoldersLevels(folderToDrop, allowedLevel)) {
      return t(
        'Cannot move here. This folder contains a subfolder, which would exceed the two-level nesting limit.'
      );
    }

    return null;
  }, [dropFolder, dragItem, t]);

  const onHoverIn = useCallback(
    (item: AutomationOrFolder) => {
      if (!isLocked) {
        setDragItem(item);
      }
    },
    [isLocked]
  );

  const onHoverOut = useCallback(() => {
    if (!isLocked) {
      setDragItem(null);
    }
  }, [isLocked]);

  const onHoverInDropZone = useCallback(
    (folderStructure: FolderStruct<Folder>) => {
      if (dragging) {
        setDropFolder(folderStructure);
      }
    },
    [dragging]
  );

  const onHoverOutDropZone = useCallback(() => {
    if (dragging) {
      setDropFolder(null);
    }
  }, [dragging]);

  const onDragStart = useCallback(() => {
    if (dragItem) {
      setDragging(true);
    }
  }, [dragItem]);

  const onDragEnd = useCallback(async () => {
    setDragging(false);
    setDropFolder(null);

    if (notAllowedMessage) {
      setDragItem(null);
      return showToast({
        message: notAllowedMessage,
        variant: toastVariant.FAILURE,
      });
    }

    if (dragItem && dropFolder) {
      setIsPatching(true);
      try {
        if (isFolder(dragItem)) {
          const response = await patchAutomationFolder(
            AxiosService,
            dragItem.id,
            { parent_folder_id: dropFolder.id, name: dragItem.name }
          );

          dispatch(
            editFolderSuccess({
              folder: {
                ...response,
                automations_count: dragItem.automations_count,
              },
              edits: { parent_folder_id: dropFolder.id },
            })
          );
        } else {
          await patchAutomation(AxiosService, dragItem.id, {
            folder_id: dropFolder.id,
            last_revision: dragItem.revision,
          });
        }
        dispatch(getAutomationsAndFolders());
      } catch (err) {
        const message =
          getErrorMessage(getOriginalError(err)) ||
          t('An error occurred while moving the item. Please try again.');
        showToast({ message, variant: toastVariant.FAILURE });
      } finally {
        setIsPatching(false);
      }
    } else {
      setDragItem(null);
    }
  }, [dragItem, dropFolder, dispatch, notAllowedMessage, showToast, t]);

  useEffect(() => {
    if (!patching) {
      setDragItem(null);
    }
  }, [patching]);

  return (
    <>
      {dragging && <GlobalDraggingStyles styles={styles} />}
      <FoldersDragAndDropContext.Provider
        value={{
          dragging,
          patching,
          isAllowed: !notAllowedMessage,
          dragItem,
          dropFolder,
          onDragStart,
          onDragEnd,
          onHoverIn,
          onHoverOut,
          onHoverInDropZone,
          onHoverOutDropZone,
        }}
      >
        {children}
      </FoldersDragAndDropContext.Provider>
    </>
  );
};
